import React, { useState } from "react";
import PropTypes from 'prop-types';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SyncIcon from '@mui/icons-material/Sync';
import Theme from './Theme'; // Assuming Theme is in the same directory

const NavbarComponent = (props) => {
  const { currentUser, companies, currentCompany: initialCompany, csrfToken } = props;

  const [anchorCompanyEl, setAnchorCompanyEl] = useState(null);
  const [anchorUserEl, setAnchorUserEl] = useState(null);
  // Potentially manage currentCompany state here if switching needs to re-render only navbar
  // Or lift state up if HomePage needs to know the selected company
  const [currentCompany, setCurrentCompany] = useState(initialCompany);

  const openCompanyMenu = Boolean(anchorCompanyEl);
  const openUserMenu = Boolean(anchorUserEl);

  const handleCompanyClick = (event) => {
    setAnchorCompanyEl(event.currentTarget);
  };
  const handleCompanyClose = () => {
    setAnchorCompanyEl(null);
  };

  const handleUserClick = (event) => {
    setAnchorUserEl(event.currentTarget);
  };
  const handleUserClose = () => {
    setAnchorUserEl(null);
  };

  // Placeholder handlers
  const handleChangeOrganization = () => {
    console.log("Change organization clicked");
    handleCompanyClose();
    // TODO: Implement logic to show company list menu/modal using 'companies' prop
    // This might involve lifting state up or calling a prop function
  };
  const handleFilesClick = () => { console.log("Files clicked"); handleCompanyClose(); };
  const handleSettingsClick = () => { console.log("Settings clicked"); handleCompanyClose(); };
  const handleUsersClick = () => { console.log("Users clicked"); handleCompanyClose(); };
  const handleIntegrationsClick = () => { console.log("Integrations clicked"); handleCompanyClose(); };
  const handleUserSettings = () => { console.log("User Settings clicked"); handleUserClose(); };
  const handleLogout = () => {
    console.log("Logout clicked");
    handleUserClose();
    fetch('/users/sign_out', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      }
    }).then(response => {
      if (response.ok || response.status === 401 || response.status === 404) {
        window.location.href = '/users/sign_in';
      } else {
        console.error('Logout failed:', response);
        alert('Logout failed. Please try again.');
      }
    }).catch(error => {
      console.error('Error during logout:', error);
      alert('An error occurred during logout.');
    });
  };

  // Helper to get user initials for Avatar
  const getUserInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  return (
    <Theme>
      <AppBar position="fixed" elevation={0} sx={{ backgroundColor: '#3b82f6', zIndex: 1300 }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Left Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Logo */}
            <img src="/logo-white.png" alt="Logo" style={{ height: '32px', marginRight: '8px' }} />

            {/* Company Menu Button - Only show if a company exists */}
            {currentCompany && (
              <Button
                id="company-button"
                aria-controls={openCompanyMenu ? 'company-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={openCompanyMenu ? 'true' : undefined}
                onClick={handleCompanyClick}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.12)'
                  },
                  textTransform: 'none',
                  fontSize: '1rem',
                  minWidth: '150px',
                  justifyContent: 'space-between'
                }}
              >
                {currentCompany.name}
              </Button>
            )}
            {/* Company Menu Dropdown */}
            <Menu
              id="company-menu"
              anchorEl={anchorCompanyEl}
              open={openCompanyMenu}
              onClose={handleCompanyClose}
              MenuListProps={{ 'aria-labelledby': 'company-button' }}
              PaperProps={{ sx: { mt: 1, minWidth: 250, boxShadow: 3 } }}
            >
              {/* Conditionally render "Change organization" */}
              {companies && companies.length > 1 && (
                <MenuItem onClick={handleChangeOrganization}>
                  <ListItemIcon><SyncIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Change organization</ListItemText>
                </MenuItem>
              )}
              {companies && companies.length > 1 && <Divider />}

              <MenuItem onClick={handleFilesClick}>Files</MenuItem>
              <MenuItem onClick={handleSettingsClick}>Settings</MenuItem>
              <MenuItem onClick={handleUsersClick}>Users</MenuItem>
              <MenuItem onClick={handleIntegrationsClick}>Integrations</MenuItem>
            </Menu>

            {/* Navigation Links */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                href={`/?company_id=${currentCompany?.id}`} 
                sx={{ color: 'white', textTransform: 'none' }}
              >
                Dashboard
              </Button>
              <Button 
                href={`/contacts?company_id=${currentCompany?.id}`} 
                sx={{ color: 'white', textTransform: 'none' }}
              >
                Contacts
              </Button>
              <Button 
                href={`/wallets?company_id=${currentCompany?.id}`} 
                sx={{ color: 'white', textTransform: 'none' }}
              >
                Wallets
              </Button>
              <Button 
                href={`/inventory?company_id=${currentCompany?.id}`} 
                sx={{ color: 'white', textTransform: 'none' }}
              >
                Inventory
              </Button>
            </Box>
          </Box>

          {/* Right Section (User Menu) */}
          <Box>
            <Button
              id="user-button"
              onClick={handleUserClick}
              size="small"
              aria-controls={openUserMenu ? 'user-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={openUserMenu ? 'true' : undefined}
              sx={{ color: 'white', textTransform: 'none', padding: '0px 8px' }}
              startIcon={<Avatar sx={{ width: 32, height: 32, bgcolor: 'lightgray', fontSize: '0.9rem' }}>{getUserInitials(currentUser?.name)}</Avatar>}
              endIcon={<KeyboardArrowDownIcon />}
            >
              {currentUser?.name || currentUser?.email}
            </Button>
            {/* User Menu Dropdown */}
            <Menu
              id="user-menu"
              anchorEl={anchorUserEl}
              open={openUserMenu}
              onClose={handleUserClose}
              MenuListProps={{ 'aria-labelledby': 'user-button' }}
              PaperProps={{ sx: { mt: 1, minWidth: 200, boxShadow: 3 } }}
            >
              <MenuItem disabled sx={{ opacity: '1 !important' }}>
                <ListItemText primary={currentUser?.name} secondary={currentUser?.email} />
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleUserSettings}>Settings</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
    </Theme>
  );
};

NavbarComponent.propTypes = {
  currentUser: PropTypes.shape({
    id: PropTypes.number.isRequired,
    email: PropTypes.string.isRequired,
    name: PropTypes.string
  }),
  companies: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  })).isRequired,
  currentCompany: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }),
  csrfToken: PropTypes.string.isRequired
};

export default NavbarComponent; 
